import type { DraftAdjustment } from "~/components/demand-2/demandDraftState"
import { insightLabelFor, type NoosMetricKey } from "~/components/demand-2/shared/noosBreakdown"
import { FRAMING_SIGNAL_THEME, isFramingSignalKey } from "~/utils/demandFramingSignal"
import { INSIGHT_PREFIX, isStrategicInsightKey } from "~/utils/demandInsightValues"
import { PREORDER_FLOOR_KEY } from "~/utils/demandProjection"
import { isCollectionWide } from "~/utils/demandScope"
import { formatName } from "~/utils/namingUtils"

// One adjustment as the grid's hover shows it: what was authored, plus the units it moved.
export type AdjustmentImpact = {
  id: string
  // One logical adjustment applied to N variants; the merge key in `aggregateImpacts`.
  groupId?: string | null
  name: string
  theme: string
  type: DraftAdjustment["type"]
  value: number
  /**
   * Units from the engine's per-stage attribution. `null` = no figure reported (a scope that
   * reached no leaf, or a read catching up), NOT 0. Never filled in locally: sizing needs the
   * server-side leaf cube.
   */
  units: number | null
  startDate: Date | null
  endDate: Date | null
  scopeLabel?: string
  collectionWide: boolean
  // An accepted DS insight (DemandSetupInsight); `value` is the figure frozen at accept time.
  insight?: boolean
  // The Cannibalization signal derived from the Framing answer, with no stored copy.
  derived?: boolean
  // Aggregated rows only: variants carrying this row, and whether they disagreed on `value`.
  variantCount?: number
  ambiguousValue?: boolean
}

type ImpactSeed = Omit<AdjustmentImpact, "units">

export type FramingImpactSeed = { id: string; name: string; pct: number | null }

/** The insight key behind an attribution key, or null for a planner row or the framing signal. */
const insightKeyOf = (attrKey: string): NoosMetricKey | null =>
  attrKey.startsWith(INSIGHT_PREFIX)
    ? (attrKey.slice(INSIGHT_PREFIX.length) as NoosMetricKey)
    : null

/**
 * Model insights are the keys the model both sizes and decides; everything else in Demand signals
 * is Strategy. Every surface cuts the stage with this one predicate.
 */
export const isModelInsightKey = (attrKey: string): boolean => {
  const key = insightKeyOf(attrKey)
  return key != null && !isStrategicInsightKey(key)
}

/**
 * Strategy's Strategic assumptions half: what Demand configuration set (Cannibalization, growth) and
 * the pre-order floor the bookings set. Every other Strategy key is a Strategic adjustment.
 */
export const isStrategicAssumptionKey = (attrKey: string): boolean =>
  isFramingSignalKey(attrKey) ||
  attrKey === PREORDER_FLOOR_KEY ||
  (insightKeyOf(attrKey) != null && !isModelInsightKey(attrKey))

// `collectionWide` = a `VARIANT_ALL` key sized for this variant, not a value it froze at accept.
export type SetupInsightSeed = {
  insightKey: string
  value: number
  collectionWide?: boolean
}

const attribute = (seeds: ImpactSeed[], attribution: Record<string, number>): AdjustmentImpact[] =>
  seeds.map((seed) => ({ ...seed, units: attribution[seed.id] ?? null }))

const seedFromAdjustment = (
  a: DraftAdjustment,
  scopeLabelFor?: (a: DraftAdjustment) => string | null,
): ImpactSeed => ({
  id: a.id,
  groupId: a.groupId,
  name: a.name,
  theme: a.theme,
  type: a.type,
  value: a.value,
  startDate: a.startDate ?? null,
  endDate: a.endDate ?? null,
  scopeLabel: scopeLabelFor?.(a) ?? undefined,
  collectionWide: isCollectionWide(a),
})

/**
 * The on-top stack in engine order. Σ units is the on-top delta less any clamp residual
 * (`ATTRIBUTION_RESIDUAL` matches no row, so is never listed).
 */
export const toAdjustmentImpacts = (
  adjs: DraftAdjustment[],
  attribution: Record<string, number>,
  scopeLabelFor?: (a: DraftAdjustment) => string | null,
): AdjustmentImpact[] =>
  attribute(
    adjs.map((a) => seedFromAdjustment(a, scopeLabelFor)),
    attribution,
  )

/**
 * Rows the card cannot tell apart are one row: `groupId`, else everything the card renders. Not the
 * row id (one adjustment authored per variant would repeat) and not `value` (see `ambiguousValue`).
 */
const mergeKey = (r: AdjustmentImpact): string =>
  r.groupId ??
  (r.insight || r.derived
    ? r.id
    : [
        r.name,
        r.theme,
        r.startDate?.getTime() ?? "",
        r.endDate?.getTime() ?? "",
        r.scopeLabel ?? "",
        r.collectionWide,
      ].join("|"))

// All-unsized stays null, so a merged row reads as unsized rather than as a real zero.
const addUnits = (a: number | null, b: number | null): number | null =>
  a == null ? b : b == null ? a : a + b

// -1 sorts unsized rows last; at the top they would read as "biggest".
const magnitudeOf = (r: AdjustmentImpact): number => (r.units == null ? -1 : Math.abs(r.units))

/**
 * A stage's stack for a GROUP of variants, one line per `mergeKey`, by magnitude. `units` sums;
 * `value` does NOT (percentages don't add), so disagreeing contributors flag `ambiguousValue`.
 * `variantCount` counts VARIANTS, not rows.
 */
export const aggregateImpacts = (perVariant: AdjustmentImpact[][]): AdjustmentImpact[] => {
  const merged = new Map<string, AdjustmentImpact>()
  for (const list of perVariant) {
    const countedHere = new Set<string>()
    for (const r of list) {
      const key = mergeKey(r)
      const firstForThisVariant = !countedHere.has(key)
      countedHere.add(key)
      const hit = merged.get(key)
      if (!hit) {
        merged.set(key, { ...r, variantCount: 1 })
        continue
      }
      hit.units = addUnits(hit.units, r.units)
      if (firstForThisVariant) hit.variantCount = (hit.variantCount ?? 1) + 1
      if (hit.type !== r.type || hit.value !== r.value) hit.ambiguousValue = true
    }
  }
  // Name tiebreak: insertion order moves with the group's sorting/filtering.
  return [...merged.values()].sort(
    (a, b) => magnitudeOf(b) - magnitudeOf(a) || a.name.localeCompare(b.name),
  )
}

// A function: `formatName` reads tenant naming at render.
export const preorderFloorName = () => `Confirmed ${formatName("preorder", true)} floor`

/**
 * The Demand signals stage in the order `projectDemandForVariant` folds it, which the card's
 * numbering claims: model insights, Cannibalization signal, strategic insights, setup adjustments.
 * `insights` must include the collection-wide keys sized for this variant, or rows go missing.
 */
export const toSetupImpacts = (
  insights: SetupInsightSeed[],
  adjs: DraftAdjustment[],
  attribution: Record<string, number>,
  scopeLabelFor?: (a: DraftAdjustment) => string | null,
  framing: FramingImpactSeed[] = [],
): AdjustmentImpact[] => {
  // Biggest first and deterministic: the list read is unordered.
  const insightSeeds = (rows: SetupInsightSeed[]): ImpactSeed[] =>
    [...rows]
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .map((i) => ({
        id: `${INSIGHT_PREFIX}${i.insightKey}`,
        // Derived at render so it reads the tenant's current naming.
        name: insightLabelFor(i.insightKey as NoosMetricKey),
        theme: "",
        type: "add" as const,
        value: i.value,
        startDate: null,
        endDate: null,
        collectionWide: i.collectionWide ?? false,
        // A strategic insight is the planner's decision, so no "sized from the model" marker.
        insight: !isStrategicInsightKey(i.insightKey as NoosMetricKey),
      }))
  const isStrategic = (i: SetupInsightSeed) => isStrategicInsightKey(i.insightKey as NoosMetricKey)
  const floorUnits = attribution[PREORDER_FLOOR_KEY] ?? 0
  return attribute(
    [
      ...insightSeeds(insights.filter((i) => !isStrategic(i))),
      ...framing.map(
        (f): ImpactSeed => ({
          id: f.id,
          name: f.name,
          theme: FRAMING_SIGNAL_THEME,
          type: "percentage",
          value: f.pct ?? 0,
          startDate: null,
          endDate: null,
          collectionWide: false,
          derived: true,
        }),
      ),
      ...insightSeeds(insights.filter(isStrategic)),
      ...adjs.map((a) => seedFromAdjustment(a, scopeLabelFor)),
      ...(Math.round(floorUnits) !== 0
        ? [
            {
              id: PREORDER_FLOOR_KEY,
              name: preorderFloorName(),
              theme: "",
              type: "add" as const,
              value: floorUnits,
              startDate: null,
              endDate: null,
              collectionWide: false,
            },
          ]
        : []),
    ],
    attribution,
  )
}
